import React, { useEffect, useState } from "react";
import {
    useModalForm,
    Modal,
    Space,
    Form,
    Button,
    Divider,
    Row,
    Col,
    Switch,
    Checkbox,
    Icons,
    notificationProvider,
    Table
} from "@pankod/refine-antd";
import { ICompany } from "interfaces";
import NestsxCrud from "dataProvider/nestjs-crud";
import { API_URL } from "App";
import { AddLocationAdditionalDataModel } from "./add-location-additional-data.model";


interface ConfigureCustomWorkflowModelProps {
    refetchCompanyDetail: (shouldRefetch: boolean) => void;
    companyId: string;
    companyDetail: ICompany;
}

interface PreviewData {
    header?: string[] | null;
    validatedRows?: (({ [key: string]: string | null })[] | null)[] | null;
}

const masterTypeMap = new Map()
    .set('isDesignationRequiredForApprovalWorkflow', 'DESIGNATION')
    .set('isDepartmentRequiredForApprovalWorkflow', 'DEPARTMENT')
    // .set('isSubDepartmentRequiredForApprovalWorkflow', '')
    .set('isEmployeeGradeRequiredForApprovalWorkflow', 'EMPLOYEE_GRADE')
    // .set('isEmployeeCategoryRequiredForApprovalWorkflow', '')
    .set('isLocationRequiredForApprovalWorkflow', 'LOCATION')
    .set('isVendorCategoryRequiredForApprovalWorkflow', 'VENDOR_CATEGORY')
    // .set('isLocationAdditionalDataRequiredForApprovalWorkflow', 'LOCATION_ADDITIONAL_DATA')
    .set('isProjectRequiredForApprovalWorkflow', 'PROJECT')
    // .set('isReportingManagerRequiredForApprovalWorkflow', '');


const CONFIGURED_ACTION_ROLES = [
    { label: 'Company Admin', value: 'COMPANY_ADMIN' },
    { label: 'Accountant', value: 'COMPANY_ACCOUNTANT' },
    { label: 'Manager', value: 'COMPANY_MANAGER' },
    { label: 'All Users', value: 'USER' },
];

const VENDOR_CREATION_REQUEST_ROLES = [
    // { label: 'Company Admin', value: 'COMPANY_ADMIN' }, // Commented as per requirement
    { label: 'Accountant', value: 'COMPANY_ACCOUNTANT' },
    { label: 'Manager', value: 'COMPANY_MANAGER' },
    { label: 'Basic User', value: 'BASIC_USER' },
    { label: 'All Users', value: 'USER' },
];

const VENDOR_CREATION_REVIEW_ROLES = CONFIGURED_ACTION_ROLES.filter((role) =>
    ['COMPANY_ADMIN', 'COMPANY_ACCOUNTANT'].includes(role.value),
);

const CONFIGURED_ACTION_KEYS = ['PAYEE_MANAGEMENT', 'VENDOR_CREATION_REQUEST', 'VENDOR_CREATION_REVIEW'] as const;
type ConfiguredActionKey = typeof CONFIGURED_ACTION_KEYS[number];
type ConfiguredActionsState = Record<ConfiguredActionKey, string[]>;

const CONFIGURED_ACTION_ROLE_OPTIONS: Record<ConfiguredActionKey, { label: string; value: string }[]> = {
    PAYEE_MANAGEMENT: CONFIGURED_ACTION_ROLES,
    VENDOR_CREATION_REQUEST: VENDOR_CREATION_REQUEST_ROLES,
    VENDOR_CREATION_REVIEW: VENDOR_CREATION_REVIEW_ROLES,
};

const sanitizeConfiguredActionValues = (key: ConfiguredActionKey, values: string[] = []) => {
    const allowedValues = new Set(CONFIGURED_ACTION_ROLE_OPTIONS[key].map((role) => role.value));
    const filtered = values.filter((value) => allowedValues.has(value));
    if (filtered.includes('USER')) {
        return ['USER'];
    }
    return filtered;
};

const CONFIGURED_ACTION_CHECKBOX_GROUP_STYLE = { width: '100%' } as const;

const getDefaultConfiguredActionsState = (companyDetail: ICompany): ConfiguredActionsState => ({
    PAYEE_MANAGEMENT: sanitizeConfiguredActionValues(
        'PAYEE_MANAGEMENT',
        companyDetail.companySettings?.settings?.configuredActionsAndPermissions?.PAYEE_MANAGEMENT ?? [],
    ),
    VENDOR_CREATION_REQUEST: sanitizeConfiguredActionValues(
        'VENDOR_CREATION_REQUEST',
        companyDetail.companySettings?.settings?.configuredActionsAndPermissions?.VENDOR_CREATION_REQUEST ?? [],
    ),
    VENDOR_CREATION_REVIEW: sanitizeConfiguredActionValues(
        'VENDOR_CREATION_REVIEW',
        companyDetail.companySettings?.settings?.configuredActionsAndPermissions?.VENDOR_CREATION_REVIEW ?? [],
    ),
});

const isSameStringArray = (left: string[] = [], right: string[] = []) =>
    left.length === right.length && left.every((value, index) => value === right[index]);

const isVendorFlagsPairValid = (state: ConfiguredActionsState): boolean => {
    const hasRequest = state.VENDOR_CREATION_REQUEST.length > 0;
    const hasReview = state.VENDOR_CREATION_REVIEW.length > 0;
    return hasRequest === hasReview;
};

const isSameConfiguredActionsState = (left: ConfiguredActionsState, right: ConfiguredActionsState) =>
    CONFIGURED_ACTION_KEYS.every((key) => isSameStringArray(left[key], right[key]));

const getDefaultCheckboxState = (companyDetail: ICompany) => {
    const featureFlags = companyDetail.featureFlags;
    const companySettings = companyDetail.companySettings?.settings;
    return {
        dynamicWorkflow: featureFlags?.dynamicWorkflow,

        isDepartmentRequiredForApprovalWorkflow: (companySettings?.isDepartmentRequiredForApprovalWorkflow) || false,
        isSubDepartmentRequiredForApprovalWorkflow: (companySettings?.isSubDepartmentRequiredForApprovalWorkflow) || false,
        isDesignationRequiredForApprovalWorkflow: (companySettings?.isDesignationRequiredForApprovalWorkflow) || false,
        isEmployeeGradeRequiredForApprovalWorkflow: (companySettings?.isEmployeeGradeRequiredForApprovalWorkflow) || false,
        isEmployeeCategoryRequiredForApprovalWorkflow: (companySettings?.isEmployeeCategoryRequiredForApprovalWorkflow) || false,
        isVendorCategoryRequiredForApprovalWorkflow: (companySettings?.isVendorCategoryRequiredForApprovalWorkflow) || false,
        isLocationRequiredForApprovalWorkflow: (companySettings?.isLocationRequiredForApprovalWorkflow) || false,
        isLocationAdditionalDataRequiredForApprovalWorkflow: (companySettings?.isLocationAdditionalDataRequiredForApprovalWorkflow) || false,
        isProjectRequiredForApprovalWorkflow: (companySettings?.isProjectRequiredForApprovalWorkflow) || false,
        isReportingManagerRequiredForApprovalWorkflow: (companySettings?.isReportingManagerRequiredForApprovalWorkflow) || false,
        isVendorMasterEnabled: (companySettings?.isVendorMasterEnabled) || false,
    };
}




export const ConfigureCustomWorkflowModel: React.FC<ConfigureCustomWorkflowModelProps> = ({
    refetchCompanyDetail,
    companyId,
    companyDetail
}) => {
    const [isPreviewMasterModalVisible, setIsPreviewMasterModalVisible] = useState(false);
    const [uploadedMasterDataFile, setUploadedMasterDataFile] = useState<{
        file: File | null,
        label: string | null,
        type: string | null,
        previewData?: PreviewData | null,
    }>({
        type: null,
        label: null,
        file: null,
        previewData: null,
    });


    const defaultState = getDefaultCheckboxState(companyDetail);
    const [checkboxState, setCheckboxState] = useState(defaultState);

    const defaultConfiguredActionsState = getDefaultConfiguredActionsState(companyDetail);
    const [configuredActionsState, setConfiguredActionsState] = useState<ConfiguredActionsState>(defaultConfiguredActionsState);

    const buildConfiguredActionsAndPermissionsPayload = () => {
        const { __typename, ...existing } = companyDetail.companySettings?.settings?.configuredActionsAndPermissions || {} as any;
        const updated = { ...existing };
        for (const key of CONFIGURED_ACTION_KEYS) {
            const sanitizedValues = sanitizeConfiguredActionValues(key, configuredActionsState[key]);
            if (sanitizedValues.length > 0) {
                updated[key] = sanitizedValues;
            } else {
                delete updated[key];
            }
        }
        return updated;
    };

    const buildUpdateCompanyFeaturesAndSettingsInput = () => ({
        companyId,
        ...(checkboxState.dynamicWorkflow) && {
            companyFeatureFlags: {
                dynamicWorkflow: checkboxState.dynamicWorkflow,
            }
        },
        companySettings: {
            ...(checkboxState.isDepartmentRequiredForApprovalWorkflow) && { isDepartmentRequiredForApprovalWorkflow: checkboxState.isDepartmentRequiredForApprovalWorkflow },
            ...(checkboxState.isSubDepartmentRequiredForApprovalWorkflow) && { isSubDepartmentRequiredForApprovalWorkflow: checkboxState.isSubDepartmentRequiredForApprovalWorkflow },
            ...(checkboxState.isDesignationRequiredForApprovalWorkflow) && { isDesignationRequiredForApprovalWorkflow: checkboxState.isDesignationRequiredForApprovalWorkflow },
            ...(checkboxState.isEmployeeGradeRequiredForApprovalWorkflow) && { isEmployeeGradeRequiredForApprovalWorkflow: checkboxState.isEmployeeGradeRequiredForApprovalWorkflow },
            ...(checkboxState.isEmployeeCategoryRequiredForApprovalWorkflow) && { isEmployeeCategoryRequiredForApprovalWorkflow: checkboxState.isEmployeeCategoryRequiredForApprovalWorkflow },
            ...(checkboxState.isVendorCategoryRequiredForApprovalWorkflow) && { isVendorCategoryRequiredForApprovalWorkflow: checkboxState.isVendorCategoryRequiredForApprovalWorkflow },
            ...(checkboxState.isLocationRequiredForApprovalWorkflow) && { isLocationRequiredForApprovalWorkflow: checkboxState.isLocationRequiredForApprovalWorkflow },
            ...(checkboxState.isLocationAdditionalDataRequiredForApprovalWorkflow) && { isLocationAdditionalDataRequiredForApprovalWorkflow: checkboxState.isLocationAdditionalDataRequiredForApprovalWorkflow },
            ...(checkboxState.isProjectRequiredForApprovalWorkflow) && { isProjectRequiredForApprovalWorkflow: checkboxState.isProjectRequiredForApprovalWorkflow },
            ...(checkboxState.isReportingManagerRequiredForApprovalWorkflow) && { isReportingManagerRequiredForApprovalWorkflow: checkboxState.isReportingManagerRequiredForApprovalWorkflow },
            ...(checkboxState.isVendorMasterEnabled) && { isVendorMasterEnabled: checkboxState.isVendorMasterEnabled },
            configuredActionsAndPermissions: buildConfiguredActionsAndPermissionsPayload(),
        },
    });

    useEffect(() => {
        const nextState = getDefaultConfiguredActionsState(companyDetail);
        setConfiguredActionsState((prevState) => {
            if (isSameConfiguredActionsState(prevState, nextState)) {
                return prevState;
            }
            return nextState;
        });
    }, [
        companyDetail.companySettings?.settings?.configuredActionsAndPermissions?.PAYEE_MANAGEMENT,
        companyDetail.companySettings?.settings?.configuredActionsAndPermissions?.VENDOR_CREATION_REQUEST,
        companyDetail.companySettings?.settings?.configuredActionsAndPermissions?.VENDOR_CREATION_REVIEW,
    ]);

    // const isEveryActionEnabled =
    //     Object.values(defaultState).every(it => it === true) &&
    //     CONFIGURED_ACTION_KEYS.every((key) => defaultConfiguredActionsState[key].length > 0);


    const {
        modalProps: configureCustomWorkflowModalProps,
        formProps: configureCustomWorkflowFormProps,
        show: showConfigureCustomWorkflowModal,
        close: closeConfigureCustomWorkflowModal,
    } = useModalForm<any>({
        dataProviderName: "grapghQl",
        resource: "updateCompanyFeaturesAndSettings",
        action: "create",
        metaData: {
            input: buildUpdateCompanyFeaturesAndSettingsInput(),
        },
        redirect: false,
        onMutationSuccess() {
            refetchCompanyDetail(true);
        },
    });
    configureCustomWorkflowModalProps.afterClose = () => {
        setCheckboxState(defaultState);
        setConfiguredActionsState(defaultConfiguredActionsState);
    };
    configureCustomWorkflowModalProps.title = 'Configure Customised Workflow';
    configureCustomWorkflowModalProps.centered = true;
    configureCustomWorkflowModalProps.width = 860;

    configureCustomWorkflowModalProps.footer = (
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <Button onClick={() => closeConfigureCustomWorkflowModal()}>Cancel</Button>
            <Button
                type="primary"
                onClick={() => {
                    if (!isVendorFlagsPairValid(configuredActionsState)) {
                        notificationProvider.open({
                            message: 'Both vendor creation settings must be configured together, or both cleared.',
                            type: 'error',
                            description: 'Invalid configuration',
                        });
                        return;
                    }
                    configureCustomWorkflowFormProps.onFinish?.({
                        input: buildUpdateCompanyFeaturesAndSettingsInput(),
                    });
                }}
            >
                Save
            </Button>
        </div>
    );
    // if (isEveryActionEnabled) {
    //     configureCustomWorkflowModalProps.footer = null;
    // }


    const previewMasterData = async (file, type: string, label: string) => {
        const response = await callMasterDataApi({ file, type, label, showPreview: true });
        if (response) {
            setUploadedMasterDataFile({ file, type, label, previewData: response });
            closeConfigureCustomWorkflowModal();
            setIsPreviewMasterModalVisible(true);
        }
    }

    const uploadMasterData = async (file, type: string, label: string) => {
        const response = await callMasterDataApi({ file, type, label, showPreview: false });
        if (response) {
            notificationProvider.open({
                message: `Master data sucessfully updated for '${label}'`,
                type: 'success',
                description: `Successfully uploaded data`,
            });
        }
    }

    const callMasterDataApi = async ({ file, type, label, showPreview = true }) => {
        const showErrorToast = (err) => {
            notificationProvider.open({
                message: err?.message || err,
                type: 'error',
                description: `Failed to upload master data for '${label}'`,
            });
        };
        try {
            const dataProvider = NestsxCrud('') as any;
            if (dataProvider) {
                const formData = new FormData();
                formData.append('master', file);
                let url = `${API_URL}/approval-workflow/upload/internal`;
                if (showPreview) {
                    url = `${url}/${type}/master/preview`;
                } else {
                    url = `${url}/${companyDetail.id}/${type}/master`;
                }
                const data = await dataProvider.custom({
                    url,
                    method: 'post',
                    payload: formData,
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                });
                if (data?.data) {
                    return data.data;
                } else {
                    showErrorToast('Something went wrong');
                }
            }
        } catch (err: any) {
            showErrorToast(err);
        }
    }


    const handleCheckboxChange = (name: string, checked: boolean) => {
        setCheckboxState(prevState => ({
            ...prevState,
            [name]: checked,
        }));
    }

    const handleRoleCheckboxChange = (key: keyof typeof configuredActionsState, values: string[]) => {
        const prev = configuredActionsState[key];
        const ALL_USERS = 'USER';
        let result: string[];
        if (!prev.includes(ALL_USERS) && values.includes(ALL_USERS)) {
            result = [ALL_USERS];
        } else if (prev.includes(ALL_USERS) && values.length > 1) {
            result = values.filter(v => v !== ALL_USERS);
        } else {
            result = values;
        }
        setConfiguredActionsState(s => ({ ...s, [key]: sanitizeConfiguredActionValues(key, result) }));
    };

    const handleIconClick = (fileInputId: string): void => {
        const fileInput = document.getElementById(fileInputId) as HTMLInputElement | null;
        if (fileInput) {
            fileInput.click();
        }
    };

    const handleFileChange = (event, name, label) => {
        const resetFileData = () => setUploadedMasterDataFile({ file: null, label: null, type: null, previewData: null });

        const file = event.target.files[0] as File;
        if (file) {
            if (file.type === "text/csv") {
                setUploadedMasterDataFile({ file, type: masterTypeMap.get(name), label });
                previewMasterData(file, masterTypeMap.get(name), label);
            } else resetFileData();
        } else {
            resetFileData();
        }
    }


    const renderToggleItem = ({ label, name }: { label: string, name: string }) => {
        const fileInputId = `${name}-master-upload-input`;
        const showToggleSwitch = !defaultState[name];
        const showUploadMasterBtn = defaultState[name];
        const isLocationAdditionalData = name === 'isLocationAdditionalDataRequiredForApprovalWorkflow';

        return (
            ((showToggleSwitch) || (showUploadMasterBtn && (masterTypeMap.has(name) || isLocationAdditionalData)))
            &&
            (
                <Row justify="space-between" align="middle" style={{ paddingTop: 10 }}>
                    <Col style={{ textWrap: 'wrap' }}>{label}</Col>

                    {
                        showToggleSwitch
                        && (
                            <Col>
                                <Switch
                                    id={name}
                                    key={name}
                                    checked={checkboxState[name as keyof typeof checkboxState]}
                                    onChange={(checked) => {
                                        handleCheckboxChange(name, checked)
                                    }}
                                />
                            </Col>
                        )
                    }

                    {
                        showUploadMasterBtn && isLocationAdditionalData
                        && (
                            <Col>
                                <AddLocationAdditionalDataModel
                                    companyId={companyDetail.id}
                                    refetchCompanyDetail={refetchCompanyDetail}
                                />
                            </Col>
                        )
                    }

                    {
                        showUploadMasterBtn && masterTypeMap.has(name) && !isLocationAdditionalData
                        && (
                            <Col>
                                <Icons.UploadOutlined
                                    onClick={() => { handleIconClick(fileInputId) }}
                                    style={{ cursor: 'pointer' }}
                                />
                                <input
                                    id={fileInputId}
                                    type="file"
                                    style={{ display: 'none' }}
                                    accept=".csv"
                                    onChange={(event) => { handleFileChange(event, name, label) }}
                                />
                            </Col>
                        )
                    }

                </Row>
            )
        );
    };


    const PreviewMasterDataModalComponent = ({
        isPreviewMasterModalVisible,
        setIsPreviewMasterModalVisible,
        uploadMasterData,
        uploadedMasterDataFile,
    }: {
        isPreviewMasterModalVisible: boolean,
        setIsPreviewMasterModalVisible: React.Dispatch<React.SetStateAction<boolean>>,
        uploadMasterData: (file: any, type: string, label: string) => Promise<void>,
        uploadedMasterDataFile: {
            type: string | null,
            label: string | null,
            file: File | null,
            previewData?: PreviewData | null,
        }
    }) => {
        const handleOk = async () => {
            await uploadMasterData(uploadedMasterDataFile.file, uploadedMasterDataFile.type as string, uploadedMasterDataFile.label as string)
            setIsPreviewMasterModalVisible(false);
        };

        const handleCancel = () => {
            setIsPreviewMasterModalVisible(false);
        };

        const PreviewTable = ({ data }) => {
            if (!data) {
                return <div>No data available</div>;
            }
            const { header = [], validatedRows } = (data || {}) as PreviewData;
            const columns = (header as string[]).map((col, idx) => ({
                title: col,
                dataIndex: `cell-${idx}`,
                key: col,
            }));

            const dataSource = (validatedRows || []).map((row: any, idx) => {

                return {
                    key: `${idx}`,
                    [`cell-${0}`]: Object.values(row[0] || {})?.[0],
                    [`cell-${1}`]: Object.values(row[1] || {})?.[0],
                    [`cell-${2}`]: Object.values(row[2] || {})?.[0],
                    [`cell-${3}`]: Object.values(row[3] || {})?.[0],
                    // ...(row?.length ? row.map((r, i) => {
                    //     return {
                    //         [`cell-${i}`]:  Object.values(r)[0]
                    //     }
                    // }) : {})
                }
            });
            return (
                <Table columns={columns} dataSource={dataSource} pagination={{ pageSize: 10 }} />
            );
        };

        const [form] = Form.useForm();

        return (
            <div>
                <Modal
                    title="Preview"
                    visible={isPreviewMasterModalVisible}
                    onOk={handleOk}
                    onCancel={handleCancel}
                    okText="Upload"
                    cancelText="Cancel"
                >
                    <Form form={form} layout="vertical">
                        <PreviewTable data={uploadedMasterDataFile.previewData} />
                    </Form>
                </Modal>
            </div>
        );
    };



    return (
        <Space>
            <Button
                type="default"
                onClick={() => {
                    setCheckboxState(getDefaultCheckboxState(companyDetail));
                    setConfiguredActionsState(getDefaultConfiguredActionsState(companyDetail));
                    showConfigureCustomWorkflowModal();
                }}
            >
                {companyDetail.featureFlags?.dynamicWorkflow ? 'Configure' : 'Enable'} Customised Workflow
            </Button>

            <Modal {...configureCustomWorkflowModalProps} >
                <Form {...configureCustomWorkflowFormProps} layout="vertical">
                  <div style={{ maxHeight: 'calc(80vh - 140px)', overflowY: 'auto', paddingRight: 4 }}>
                    <Form.Item>
                        <Row gutter={24} align="top">
                            <Col span={12}>
                                {
                                    (!companyDetail.featureFlags?.dynamicWorkflow)
                                    && (
                                        renderToggleItem({
                                            label: `Enable Customised Workflow for ${companyDetail.registeredName}`,
                                            name: "dynamicWorkflow"
                                        })
                                    )
                                }

                                {
                                    (!companyDetail.featureFlags?.dynamicWorkflow)
                                    && (
                                        <Divider orientation="center" style={{ paddingTop: 10 }}>Configure Fields</Divider>
                                    )
                                }

                                {renderToggleItem({ label: "Designation", name: "isDesignationRequiredForApprovalWorkflow" })}
                                {renderToggleItem({ label: "Department", name: "isDepartmentRequiredForApprovalWorkflow" })}
                                {renderToggleItem({ label: "Sub-Department", name: "isSubDepartmentRequiredForApprovalWorkflow" })}
                                {renderToggleItem({ label: "Employee Grade", name: "isEmployeeGradeRequiredForApprovalWorkflow" })}
                                {renderToggleItem({ label: "Employee Category", name: "isEmployeeCategoryRequiredForApprovalWorkflow" })}
                                {renderToggleItem({ label: "Vendor Category", name: "isVendorCategoryRequiredForApprovalWorkflow" })}
                                {renderToggleItem({ label: "Location", name: "isLocationRequiredForApprovalWorkflow" })}
                                {renderToggleItem({ label: "Location Additional Data", name: "isLocationAdditionalDataRequiredForApprovalWorkflow" })}
                                {renderToggleItem({ label: "Project", name: "isProjectRequiredForApprovalWorkflow" })}
                                {renderToggleItem({ label: "Reporting Manager", name: "isReportingManagerRequiredForApprovalWorkflow" })}
                                {renderToggleItem({ label: "Vendor Master", name: "isVendorMasterEnabled" })}
                            </Col>

                            <Col span={12}>
                                <Divider orientation="left" orientationMargin={0} style={{ marginTop: 0 }}>Configured Actions & Permissions</Divider>

                                <div style={{ border: '1px solid #f0f0f0', borderRadius: 6, padding: '12px 16px', marginBottom: 10 }}>
                                    <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 10, color: '#333' }}>Payee Management</div>
                                    <Checkbox.Group
                                        style={CONFIGURED_ACTION_CHECKBOX_GROUP_STYLE}
                                        value={configuredActionsState.PAYEE_MANAGEMENT}
                                        onChange={(values) => handleRoleCheckboxChange('PAYEE_MANAGEMENT', values as string[])}
                                    >
                                        <Row gutter={[16, 10]}>
                                            {CONFIGURED_ACTION_ROLE_OPTIONS.PAYEE_MANAGEMENT.map(role => (
                                                <Col span={12} key={role.value}>
                                                    <Checkbox value={role.value}>{role.label}</Checkbox>
                                                </Col>
                                            ))}
                                        </Row>
                                    </Checkbox.Group>
                                </div>

                                <div style={{ border: '1px solid #f0f0f0', borderRadius: 6, padding: '12px 16px', marginBottom: 10 }}>
                                    <div style={{ fontSize: 13, fontWeight: 600, color: '#333' }}>Who can raise vendor creation request</div>
                                    <div
                                        style={{
                                            fontSize: 11,
                                            color: '#888',
                                            marginBottom: 10,
                                            whiteSpace: 'normal',
                                            overflowWrap: 'anywhere',
                                            wordBreak: 'break-word',
                                            lineHeight: 1.4,
                                        }}
                                    >
                                        Requires Vendor Master to be enabled. Enabled Vendor creation request feature.
                                    </div>
                                    <Checkbox.Group
                                        style={CONFIGURED_ACTION_CHECKBOX_GROUP_STYLE}
                                        value={configuredActionsState.VENDOR_CREATION_REQUEST}
                                        onChange={(values) => handleRoleCheckboxChange('VENDOR_CREATION_REQUEST', values as string[])}
                                    >
                                        <Row gutter={[16, 10]}>
                                            {CONFIGURED_ACTION_ROLE_OPTIONS.VENDOR_CREATION_REQUEST.map(role => (
                                                <Col span={12} key={role.value}>
                                                    <Checkbox value={role.value}>{role.label}</Checkbox>
                                                </Col>
                                            ))}
                                        </Row>
                                    </Checkbox.Group>
                                </div>

                                <div style={{ border: '1px solid #f0f0f0', borderRadius: 6, padding: '12px 16px' }}>
                                    <div style={{ fontSize: 13, fontWeight: 600, color: '#333' }}>Who can directly create and approve vendors</div>
                                    <div
                                        style={{
                                            fontSize: 11,
                                            color: '#888',
                                            marginBottom: 10,
                                            whiteSpace: 'normal',
                                            overflowWrap: 'anywhere',
                                            wordBreak: 'break-word',
                                            lineHeight: 1.4,
                                        }}
                                    >
                                        Requires Vendor Master to be enabled. Enables Approve/Reject for vendor creation requests.
                                    </div>
                                    <Checkbox.Group
                                        style={CONFIGURED_ACTION_CHECKBOX_GROUP_STYLE}
                                        value={configuredActionsState.VENDOR_CREATION_REVIEW}
                                        onChange={(values) => handleRoleCheckboxChange('VENDOR_CREATION_REVIEW', values as string[])}
                                    >
                                        <Row gutter={[16, 10]}>
                                            {CONFIGURED_ACTION_ROLE_OPTIONS.VENDOR_CREATION_REVIEW.map(role => (
                                                <Col span={12} key={role.value}>
                                                    <Checkbox value={role.value}>{role.label}</Checkbox>
                                                </Col>
                                            ))}
                                        </Row>
                                    </Checkbox.Group>
                                </div>
                            </Col>
                        </Row>
                    </Form.Item>
                  </div>
                </Form>
            </Modal>

            <PreviewMasterDataModalComponent
                isPreviewMasterModalVisible={isPreviewMasterModalVisible}
                setIsPreviewMasterModalVisible={setIsPreviewMasterModalVisible}
                uploadMasterData={uploadMasterData}
                uploadedMasterDataFile={uploadedMasterDataFile}
            />


        </Space>
    );
};
